import { mapState } from 'vuex'
import popup from '@/lib/popup.js'
import { currentUser } from '@/lib/user.js'
import { io } from 'socket.io-client'
import { startIntroQuizCreator } from '@/lib/intro.js'
import introJsData from '@/mixins/introjs-data'


export default {
    mixins: [introJsData],

    data() {
        return {
            socket: null,
            isKicked: false,
            redisUser: null,
            editorConnection: null,
            requestorConnection: null,
            requestUser: null,
            stampAvailable: false,
            stampTimeout: null,
            activeTime: 5
        }
    },

    watch: {
        shareSetting: {
            async handler(value) {
                await this.$nextTick()

                if (value.canEdit) {
                    if (this.socket || this.editorConnection || this.requestorConnection) return
                    this.watcherInit()
                } else {
                    this.watcherExit()
                }
            },

            deep: true
        },
    },

    computed: {
        ...mapState({
            quiz: state => state.question,
            shareSetting: state => state.shareSetting
        })
    },

    methods: {
        async watcherInit() {
            this.$store.commit('enableToEditQuiz', false)

            this.socket = io(process.env.VUE_APP_WS_URL, {
                reconnectionAttempts: 3,
                transports: ['websocket', 'polling']
            })

            this.socket.on('connect', () => {
                this.socket.emit('quiz-creator-init', this.quiz._id, {
                    id: currentUser().id,
                    name: currentUser().name
                })

                this.socket.on('editing', editor => {
                    popup.confirm(
                        this.$t('quiz_creator_socket_send_request_edit', { name: editor.name }),
                        () => {
                            // Yes
                            this.initRequestor()

                            popup.alert(this.$t('quiz_creator_socket_waiting_request'), () => {
                                this.requestorConnection.emit('cancel-request', this.quiz._id, {
                                    id: currentUser().id,
                                    name: currentUser().name
                                })

                                this.closeModal()
                            }, {
                                closeOnClickOutside: false,
                                closeOnEsc: false,
                                buttons: {
                                    ok: {
                                        text: this.$t('quiz_creator_socket_button_cancel'),
                                        className: 'btn default font-opensans text-md'
                                    }
                                }
                            })
                        },
                        () => {
                            this.closeModal()
                        }, {
                        closeOnClickOutside: false,
                        closeOnEsc: false,
                        buttons: {
                            ok: {
                                text: this.$t('quiz_creator_socket_button_request'),
                                className: 'btn btn-custom-padding font-opensans text-md system-button-color system-button-text-color',
                            }
                        },

                        className: 'confirm-request-edit'
                    }
                    )
                })

                this.socket.on('can-join', () => {
                    this.initEditor()
                })
            })

            this.socket.on('connect_error', () => {
                this.$store.commit('enableToEditQuiz', true)
            })
        },

        initEditor() {
            if (this.requestorConnection) {
                this.requestorConnection.removeAllListeners()
                this.requestorConnection.disconnect()
                this.requestorConnection = null
            }

            this.redisUser = {
                id: currentUser().id,
                name: currentUser().name
            }

            this.editorConnection = io(`${process.env.VUE_APP_WS_URL}/socket/quiz-creator/editor`)

            this.editorWatchers()

            this.$store.commit('enableToEditQuiz', true)

            setTimeout(() => {
                startIntroQuizCreator(this.stepTour2)
            }, 500)
        },

        editorWatchers() {
            this.editorConnection.on('can-join', () => {
                this.editorConnection.on('kick', currentUser => {
                    this.isKicked = true

                    const previousUser = this.redisUser

                    popup.alert(this.$t(previousUser.id === currentUser.id ? 'quiz_creator_socket_unable_edit_another_window' : 'quiz_creator_socket_unable_edit'), () => {
                        this.removeCacheQuiz(this.quiz)
                        this.closeModalAnimation()
                        this.introHint.removeHints()
                    }, {
                        closeOnClickOutside: false,
                        closeOnEsc: false
                    })
                })

                this.editorConnection.on('enable-listen-active', () => {
                    this.stampTimeout = setTimeout(() => {
                        this.stampAvailable = true
                    }, this.activeTime * 60 * 1000)

                    document.addEventListener('mousemove', this.setStatusActive);
                })

                this.editorConnection.on('send-request', requestUser => {
                    this.requestUser = requestUser

                    popup.confirm(
                        this.$t('quiz_creator_socket_requestor_request_edit', { name: requestUser.name }),
                        () => {
                            // Yes
                            this.closeModal(requestUser)
                        },
                        () => {
                            // No
                            this.editorConnection.emit('cancel-requestor-request', this.quiz._id, requestUser)
                        },
                        {
                            closeOnClickOutside: false,
                            closeOnEsc: false,
                            buttons: {
                                ok: {
                                    text: this.$t('quiz_creator_socket_button_accept'),
                                    className: 'btn btn-custom-padding font-opensans text-md system-button-color system-button-text-color'
                                },
                                reject: {
                                    text: this.$t('quiz_creator_socket_button_reject'),
                                    className: 'btn default font-opensans text-md'
                                }
                            }
                        }
                    )
                })

                this.editorConnection.on('requestor-cancel-request', requestUser => {
                    if (requestUser.id !== this.requestUser.id) return
                    popup.close()
                })

                this.editorConnection.on('exit', () => {
                    this.watcherDisconnect()
                })

                this.editorConnection.emit('join', this.quiz._id, {
                    id: currentUser().id,
                    name: currentUser().name
                })
            })

            this.editorConnection.emit('kick-other-users', this.quiz._id, this.redisUser)
        },

        initRequestor() {
            this.requestorConnection = io(`${process.env.VUE_APP_WS_URL}/socket/quiz-creator/requestor`)
            this.requestorWatcher()
        },

        requestorWatcher() {
            this.requestorConnection.on('accept-to-join', async user => {
                if (!user || user.id !== currentUser().id) return

                popup.close()

                await this.getQuiz()

                this.requestorConnection.emit('join')

                this.requestorConnection.on('join-editor', () => {
                    this.initEditor()
                })
            })

            this.requestorConnection.on('editor-cancel-request', user => {
                if (!user || user.id !== currentUser().id) return

                popup.close()

                popup.alert(this.$t('quiz_creator_socket_cancelled_request'), () => {
                    this.closeModal()
                })
            })

            this.requestorConnection.on('exit', () => {
                this.watcherDisconnect()
            })

            this.requestorConnection.emit('request-to-edit', this.quiz._id, {
                id: currentUser().id,
                name: currentUser().name
            })
        },

        async acceptRequest(requestUser) {
            this.editorConnection.emit('accept-request', this.quiz._id, requestUser)
        },

        setStatusActive() {
            if (!this.stampAvailable) return

            clearTimeout(this.stampTimeout)

            this.editorConnection.emit('editor-active', this.quiz._id, {
                id: currentUser().id,
                name: currentUser().name
            })

            this.stampAvailable = false

            this.stampTimeout = setTimeout(() => {
                this.stampAvailable = true
            }, this.activeTime * 60 * 1000)
        },

        watcherExit() {
            if (this.editorConnection) {
                this.editorConnection.emit('editor-exit', this.quiz._id, !this.isKicked ? true : false)
            }

            if (this.requestorConnection) {
                this.requestorConnection.emit('requestor-exit', this.quiz._id)
            }

            if (this.socket) {
                this.socket.removeAllListeners()
                this.socket.disconnect()
                this.socket = null
            }
        },

        watcherDisconnect() {
            if (this.editorConnection) {
                this.editorConnection.removeAllListeners()
                this.editorConnection.disconnect()
                this.editorConnection = null
            }

            if (this.requestorConnection) {
                this.requestorConnection.removeAllListeners()
                this.requestorConnection.disconnect()
                this.requestorConnection = null
            }

            document.removeEventListener('mousemove', this.setStatusActive);
        }
    }
}
